/**
 * The brands an asset can be filed under, and whether Crab Place's
 * customer-facing brand rules apply to work built for them.
 *
 * THIS LIST IS THE ONE DEFINITION. It used to be a binary, written out by hand
 * at a dozen call sites as `brand === 'crab-and-cruise' ? 'C&C' : 'CP'`. That
 * shape is why a third brand could not simply be added: every badge, filter,
 * count and picker would have rendered it as CRAB PLACE, so the asset would
 * look correctly filed and be wrong. Not knowing which brand something belongs
 * to and knowing it is Crab Place are different answers, and the old code could
 * only give the second one. Add a brand HERE and the library picks it up.
 *
 * `brandRules` is the deliberate opt-out Greg asked for on 2026-09-08. It is a
 * RECORDED DECISION, never inferred from the brand name: Crab & Cruise is our
 * own brand and still follows our rules, while Tangier Island Cruises is a
 * separate company we produce content for, where Montserrat and our navy and
 * orange would be actively wrong rather than merely absent. Anything new
 * defaults to true - an exclusion has to be chosen.
 */
export const BRANDS = [
  { value: 'crab-place', label: 'Crab Place', short: 'CP', brandRules: true },
  { value: 'crab-and-cruise', label: 'Crab & Cruise', short: 'C&C', brandRules: true },
  { value: 'tangier-island-cruises', label: 'Tangier Island Cruises', short: 'TIC', brandRules: false },
];

export const BRAND_VALUES = BRANDS.map((b) => b.value);

const BY_VALUE = BRANDS.reduce((acc, b) => { acc[b.value] = b; return acc; }, {});

export function brandInfo(value) {
  return BY_VALUE[value] || null;
}

// An unrecognised brand keeps its own raw value rather than being folded into
// Crab Place. A row we cannot classify has to read as unclassified.
export function brandLabel(value) {
  return BY_VALUE[value]?.label || value || 'Unknown';
}

export function brandShort(value) {
  if (BY_VALUE[value]) return BY_VALUE[value].short;
  return value ? String(value).slice(0, 3).toUpperCase() : '?';
}

// Default TRUE, including for a brand this file has never heard of: forgetting
// to register a brand must not silently switch our brand rules off.
export function followsBrandRules(value) {
  return BY_VALUE[value] ? BY_VALUE[value].brandRules : true;
}
